import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, RefreshCw, Loader2, X, CreditCard, ArrowUpDown, Package, User, Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  paid:       'bg-green-500/10 text-green-500 border-green-500/20',
  refunded:   'bg-red-500/10 text-red-500 border-red-500/20',
  pending:    'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  no_payment: 'bg-muted/50 text-muted-foreground border-border',
};

function fmt(amount) {
  if (amount == null) return '--';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(val) {
  if (!val) return '--';
  return new Date(val).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
      <div className="space-y-2 rounded-lg bg-muted/30 px-4 py-3">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, highlight }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right break-all ${mono ? 'font-mono text-xs' : ''} ${highlight ? 'font-semibold text-foreground' : ''}`}>
        {value || '--'}
      </span>
    </div>
  );
}

function DetailAside({ txn, onClose }) {
  const due = txn.due_amount != null ? txn.due_amount : null;
  const dueColor = due > 0 ? 'text-red-500' : due < 0 ? 'text-yellow-500' : 'text-green-500';

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <aside
        className="relative w-full max-w-md bg-card border-l border-border shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="font-outfit font-semibold text-base">Payment Details</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Amount summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-muted/30 px-3 py-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Quoted</p>
              <p className="font-semibold text-sm">{fmt(txn.quoted_amount)}</p>
            </div>
            <div className="rounded-lg bg-green-500/10 px-3 py-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Paid</p>
              <p className="font-semibold text-sm text-green-500">{fmt(txn.amount)}</p>
            </div>
            <div className={`rounded-lg px-3 py-3 text-center ${due > 0 ? 'bg-red-500/10' : 'bg-muted/30'}`}>
              <p className="text-xs text-muted-foreground mb-1">Due</p>
              <p className={`font-semibold text-sm ${dueColor}`}>{fmt(due)}</p>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <Badge className={`text-xs px-2 py-0.5 border ${STATUS_COLORS[txn.status] || ''}`}>
              
              {txn.status === 'no_payment' 
                ? 'No Payment' 
                : (due > 0 ? 'Partially Paid' : txn.status)}
            </Badge>
            <span className="text-xs text-muted-foreground">{fmtDate(txn.created_at)}</span>
          </div>

          {/* Customer */}
          <Section title="Customer">
            <Row label="Name"  value={txn.customer_name}  highlight />
            <Row label="Email" value={txn.customer_email} />
            <Row label="Phone" value={txn.customer_phone} />
          </Section>

          {/* Carrier & Driver */}
          <Section title="Carrier & Driver">
            <Row label="Carrier" value={txn.carrier_name} highlight />
            {txn.carrier_name ? (
            <Row 
              label="Driver"  
              value={txn.driver_name ? txn.driver_name : 'Kartik Kakarala'}  
            />
          ) : (<Row 
              label="Driver"  
              value={'--'}  
            />)}
          </Section>

          {/* Load */}
          <Section title="Load">
            <Row label="Vehicle"   value={txn.vehicle}        highlight />
            <Row label="Transport" value={txn.transport_type} />
            <Row label="Route"     value={txn.pickup_zip && txn.delivery_zip ? `${txn.pickup_zip} → ${txn.delivery_zip}` : null} />
            <Row label="Status"    value={txn.load_status}    />
          </Section>

          {/* Transaction IDs */}
          <Section title="Transaction IDs">
            <Row label="Transaction"   value={txn.id}               mono />
            <Row label="Load ID"       value={txn.load_id}          mono />
            <Row label="Stripe Session" value={txn.session_id}      mono />
            <Row label="Stripe Charge" value={txn.stripe_charge_id} mono />
            {txn.refund_id     && <Row label="Refund ID"     value={txn.refund_id}     mono />}
            {txn.refund_reason && <Row label="Refund Reason" value={txn.refund_reason} />}
          </Section>

          {/* Metadata */}
          {txn.metadata && Object.keys(txn.metadata).length > 0 && (
            <Section title="Metadata">
              {Object.entries(txn.metadata).map(([k, v]) => (
                <Row key={k} label={k} value={String(v)} />
              ))}
            </Section>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function PaymentsReportPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', status: 'all' });
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState(-1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 500 });
      if (filters.start_date) params.set('start_date', filters.start_date);
      if (filters.end_date)   params.set('end_date',   filters.end_date);
      if (filters.status !== 'all') params.set('status', filters.status);
      const res = await fetch(`${API_URL}/api/admin/reports/payments?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir((d) => -d);
    else { setSortCol(col); setSortDir(-1); }
  };

  const rows = data?.transactions
    ? [...data.transactions].sort((a, b) => {
        const av = a[sortCol], bv = b[sortCol];
        if (av == null) return 1;
        if (bv == null) return -1;
        return av < bv ? sortDir : av > bv ? -sortDir : 0;
      })
    : [];

  const SortBtn = ({ col, children }) => (
    <button
      onClick={() => toggleSort(col)}
      className="flex items-center gap-1 hover:text-foreground transition-colors"
    >
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortCol === col ? 'text-accent' : 'opacity-40'}`} />
    </button>
  );

  const t = data?.totals;

  return (
    <div className="space-y-6" data-testid="payments-report">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-semibold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-accent" strokeWidth={1.5} />
            Payments Report
          </h1>
          <p className="text-muted-foreground text-sm">All loads with payment status -- click any row for full details</p>
        </div>
        <Button variant="outline" size="icon" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-card border-white/5">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" value={filters.start_date}
                onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))}
                className="bg-transparent border-border w-40" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={filters.end_date}
                onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))}
                className="bg-transparent border-border w-40" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="w-36 bg-transparent border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="no_payment">No Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Transactions', value: t.total_transactions,   icon: Package,    color: ''      },
            { label: 'Total Quoted', value: fmt(t.total_quoted),    icon: DollarSign, color: ''      },
            { label: 'Total Paid',   value: fmt(t.paid_amount),     icon: DollarSign, color: 'green' },
            { label: 'Total Due',    value: fmt(t.due_amount),      icon: DollarSign, color: t.due_amount > 0 ? 'red' : 'green' },
            { label: 'Refunded',     value: fmt(t.refunded_amount), icon: RefreshCw,  color: 'red'   },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label} className="bg-card border-white/5">
              <CardContent className="p-5 flex items-start justify-between">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{label}</p>
                  <p className={`text-xl font-outfit font-semibold ${color === 'green' ? 'text-green-500' : color === 'red' ? 'text-red-500' : ''}`}>
                    {value}
                  </p>
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color === 'green' ? 'bg-green-500/10' : color === 'red' ? 'bg-red-500/10' : 'bg-accent/10'}`}>
                  <Icon className={`h-4 w-4 ${color === 'green' ? 'text-green-500' : color === 'red' ? 'text-red-500' : 'text-accent'}`} strokeWidth={1.5} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Table */}
      <Card className="bg-card border-white/5">
        <CardHeader className="pb-0">
          <CardTitle className="font-outfit text-base">
            Transactions {data && <span className="text-muted-foreground font-normal text-sm">({rows.length})</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-16">No transactions found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-xs">
                    <th className="text-left py-3 px-4"><SortBtn col="created_at">Date</SortBtn></th>
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-left py-3 px-4">Vehicle</th>
                    {/*
                      <th className="text-left py-3 px-4">Carrier</th>
                    */}
                    <th className="text-right py-3 px-4"><SortBtn col="quoted_amount">Quoted</SortBtn></th>
                    <th className="text-right py-3 px-4"><SortBtn col="amount">Paid</SortBtn></th>
                    <th className="text-right py-3 px-4"><SortBtn col="due_amount">Due</SortBtn></th>
                    <th className="text-left py-3 px-4"><SortBtn col="status">Status</SortBtn></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((txn) => (
                    <tr
                      key={txn.id}
                      onClick={() => setSelected(txn)}
                      className="border-b border-white/5 hover:bg-muted/30 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4 whitespace-nowrap text-muted-foreground text-xs font-mono">
                        {fmtDate(txn.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium">{txn.customer_name || '--'}</p>
                        <p className="text-xs text-muted-foreground">{txn.customer_email || ''}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p>{txn.vehicle || '--'}</p>
                        {txn.pickup_zip && txn.delivery_zip && (
                          <p className="text-xs text-muted-foreground">{txn.pickup_zip} → {txn.delivery_zip}</p>
                        )}
                      </td>
                      
                      {/*<td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <span>{txn.carrier_name || <span className="text-muted-foreground text-xs">Unassigned</span>}</span>
                        </div>
                        {txn.driver_name && (
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <User className="h-3 w-3 text-muted-foreground shrink-0" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground">{txn.driver_name}</span>
                          </div>
                        )}
                      </td>*/}
                      <td className="py-3 px-4 text-right">{fmt(txn.quoted_amount)}</td>
                      <td className="py-3 px-4 text-right font-semibold text-green-500">{fmt(txn.amount)}</td>
                      <td className={`py-3 px-4 text-right font-medium ${txn.due_amount > 0 ? 'text-red-500' : txn.due_amount < 0 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                        {fmt(txn.due_amount)}
                      </td>
                      <td className="py-3 px-4" style={{ textTransform: 'capitalize' }}>
                        <Badge className={`text-xs px-2 py-0.5 border ${STATUS_COLORS[txn.status] || ''}`}>
                          {txn.status === 'no_payment' 
                            ? 'No Payment' 
                            : (txn.due_amount > 0 ? 'Partially Paid' : txn.status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {selected && <DetailAside txn={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

